import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, Fragment, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverPortal, PopoverTrigger } from '@radix-ui/react-popover';
import { cn } from '../../utils';
import { useController } from 'react-hook-form';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'cmdk';
import { CheckIcon, ChevronUpDownIcon, LoaderIcon } from '../../icons';
import { AnimatePresence, motion } from 'framer-motion';
import { useVirtualizer } from '@tanstack/react-virtual';
import { FormErrorMessage } from '../form/error-message';
import { useInView } from 'react-intersection-observer';
export function Select({ placeholder, value, options = [], icon: Icon, onChange, clearable = false, emptyState, search, async, multiple, error = false, disabled = false, }) {
    const [isOpen, setOpen] = useState(false);
    const handleClear = (e) => {
        e.stopPropagation();
        onChange(null);
    };
    const timeoutRef = useRef(null);
    const parentRef = useRef(null);
    const [internalSearch, setInternalSearch] = useState(search?.search ?? '');
    const filteredOptions = search && !async
        ? options.filter(o => o.value.toLowerCase().includes(internalSearch.toLowerCase()))
        : options;
    const isLoading = async?.isLoading || !!timeoutRef.current;
    const virtualiser = useVirtualizer({
        count: filteredOptions.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 44,
        overscan: 5,
    });
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        virtualiser.measure();
    }, [isOpen]);
    const [ref, inView] = useInView();
    useEffect(() => {
        if (!async || !inView || !async.hasNextPage) {
            return;
        }
        async.fetchNextPage();
    }, [inView]);
    const handleSearch = (value) => {
        if (!search) {
            return;
        }
        setInternalSearch(value);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        timeoutRef.current = setTimeout(() => {
            search.onSearchChange(value);
            timeoutRef.current = null;
        }, 300);
    };
    const handleSelect = (option) => {
        if (multiple) {
            let currentValues = (value ?? []);
            const optionIndex = currentValues.findIndex(v => v.id === option.id);
            if (currentValues.length === 1 && optionIndex !== -1) {
                // @ts-expect-error needs to be null to render the placeholder
                currentValues = null;
            }
            else if (optionIndex !== -1) {
                currentValues.splice(optionIndex, 1);
            }
            else {
                currentValues.push(option);
            }
            onChange(currentValues);
        }
        else {
            onChange(option);
        }
        // Handle close
        if (!multiple) {
            setOpen(false);
        }
    };
    const isSelected = (option) => {
        if (multiple) {
            return value?.some(v => v.id === option.id);
        }
        return option.id === value?.id;
    };
    return (_jsxs(Popover, { open: isOpen, onOpenChange: setOpen, children: [isOpen && (_jsx("div", { className: "fixed inset-0 z-40", onPointerDown: (e) => {
                    e.currentTarget.style.pointerEvents = 'none';
                    setOpen(false);
                } })), _jsxs(PopoverTrigger, { disabled: disabled, className: cn('focus:outline-none border border-gray-100 px-3 h-12 rounded-xl w-full text-left text-sm', 'flex items-center gap-2 text-gray-700 bg-white', {
                    'border-red-500': error,
                    'opacity-70 cursor-not-allowed': disabled,
                }), children: [Icon && (!value || Array.isArray(value) || !value.image) && _jsx(Icon, { className: "size-4" }), value
                        ? (_jsxs(Fragment, { children: [!Array.isArray(value) && value.image && (_jsx("img", { className: "size-6", src: value.image })), Array.isArray(value) ? value.map(v => v.value).join(', ') : value.value] }))
                        : (_jsx("span", { className: cn('flex items-center gap-2 text-gray-400', {
                                'text-red-300': error,
                            }), children: placeholder })), _jsxs("div", { className: "ml-auto flex items-center gap-2", children: [value && clearable && (_jsx("span", { role: "button", onClick: handleClear, className: "hover:bg-gray-50 py-1.5 px-3 rounded-xl text-gray-700", children: "Clear" })), _jsx(ChevronUpDownIcon, { className: "size-4 text-gray-600" })] })] }), _jsx(AnimatePresence, { children: _jsx(PopoverPortal, { children: _jsx(PopoverContent, { asChild: true, sideOffset: 4, align: "start", onPointerDownOutside: () => setOpen(false), children: _jsx(motion.div, { initial: { opacity: 0, scale: 0.95 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0 }, transition: { duration: 0.1 }, className: "w-[var(--radix-popover-trigger-width)] max-w-80 min-w-52 px-0 overflow-hidden bg-white border border-gray-100 rounded-2xl z-50 shadow-xl", children: _jsxs(Command, { filter: (value, search) => {
                                    if (async) {
                                        return 1;
                                    }
                                    return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                                }, children: [search && (_jsx("div", { className: "p-1.5 border-b border-gray-100", children: _jsx(CommandInput, { value: internalSearch, onValueChange: handleSearch, placeholder: search.searchPlaceholder ?? 'Search options', className: "w-full p-2.5 rounded-[10px] text-sm text-gray-700 focus:outline-indigo-400" }) })), _jsxs(CommandList, { ref: parentRef, className: "w-full overflow-auto max-h-[300px]", children: [_jsx(CommandGroup, { style: {
                                                    height: filteredOptions.length ? `${virtualiser.getTotalSize()}px` : 'auto',
                                                }, className: "w-full relative", children: virtualiser.getVirtualItems().map((virtualItem) => {
                                                    const option = filteredOptions[virtualItem.index];
                                                    return (_jsxs(CommandItem, { value: String(option.id), onSelect: () => handleSelect(option), style: {
                                                            height: `${virtualItem.size}px`,
                                                            transform: `translateY(${virtualItem.start}px)`,
                                                        }, className: cn('p-3 cursor-pointer focus:outline-none text-sm text-gray-700', 'flex items-center gap-3 w-full data-[selected=true]:bg-gray-50/50', 'absolute top-0 left-0 w-full', {
                                                            'pr-6': !isSelected(option),
                                                        }), children: [option.image && (_jsx("img", { src: option.image, className: "rounded-full w-6" })), _jsx("span", { className: "truncate", children: option.value }), isSelected(option) && (_jsx(CheckIcon, { className: "size-4 text-indigo-400 ml-auto" }))] }, option.id));
                                                }) }), !timeoutRef.current && (_jsx("div", { ref: ref })), isLoading && filteredOptions.length === 0 && (_jsx("div", { className: "flex px-2 py-2 text-sm text-gray-700 justify-center", children: _jsx(LoaderIcon, {}) })), !isLoading && (_jsx(CommandEmpty, { className: "px-2 py-3 text-sm text-gray-700 text-center", children: emptyState || 'No results found.' }))] })] }) }) }) }) })] }));
}
export function FormSelect({ name, ...props }) {
    const { field, fieldState } = useController({ name });
    return (_jsxs("div", { className: "flex flex-col w-full gap-1.5", children: [_jsx(Select, { ...props, value: field.value, onChange: value => {
                    field.onChange(value);
                    props.onChange?.(value);
                }, error: !!fieldState.error?.message }), _jsx(FormErrorMessage, { error: fieldState.error?.message })] }));
}
